import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, Download } from 'lucide-react';

export interface CompensationFilterValues {
  searchTerm: string;
  status: string;
  tier: string;
  relationship: string;
}

interface CompensationFiltersProps {
  filters: CompensationFilterValues;
  onFilterChange: (filters: CompensationFilterValues) => void;
  onExport?: () => void;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'active', label: 'Active' },
  { value: 'mentorship', label: 'Mentorship Active' },
  { value: 'no_mentor', label: 'No Mentor' },
  { value: 'inactive', label: 'Inactive' }
];

const TIER_OPTIONS = [
  { value: 'all', label: 'All Tiers' },
  { value: 'tier1', label: 'Tier 1 (>$40K)' },
  { value: 'tier2', label: 'Tier 2 ($30-40K)' },
  { value: 'tier3', label: 'Tier 3 (<$30K)' },
  { value: 'no_comp', label: 'No Compensation' }
];

const RELATIONSHIP_OPTIONS = [
  { value: 'all', label: 'All Relationships' },
  { value: 'has_override', label: 'Has Override' },
  { value: 'is_mentor', label: 'Is Mentor' },
  { value: 'has_mentor', label: 'Has Mentor' },
  { value: 'independent', label: 'Independent' }
];

export const CompensationFilters: React.FC<CompensationFiltersProps> = ({
  filters,
  onFilterChange,
  onExport
}) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      searchTerm: e.target.value
    });
  };

  const handleStatusChange = (value: string) => {
    onFilterChange({
      ...filters,
      status: value
    });
  };

  const handleTierChange = (value: string) => {
    onFilterChange({
      ...filters,
      tier: value
    });
  };

  const handleRelationshipChange = (value: string) => {
    onFilterChange({
      ...filters,
      relationship: value
    });
  };

  return (
    <div className="space-y-4 mb-6">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by name or email..."
          value={filters.searchTerm}
          onChange={handleSearchChange}
          className="pl-10"
        />
      </div>

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Status Filter */}
        <div className="w-48">
          <Select value={filters.status} onValueChange={handleStatusChange}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tier Filter */}
        <div className="w-48">
          <Select value={filters.tier} onValueChange={handleTierChange}>
            <SelectTrigger>
              <SelectValue placeholder="Compensation Tier" />
            </SelectTrigger>
            <SelectContent>
              {TIER_OPTIONS.map((tier) => (
                <SelectItem key={tier.value} value={tier.value}>
                  {tier.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Relationship Filter */}
        <div className="w-48">
          <Select value={filters.relationship} onValueChange={handleRelationshipChange}>
            <SelectTrigger>
              <SelectValue placeholder="Relationship" />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_OPTIONS.map((rel) => (
                <SelectItem key={rel.value} value={rel.value}>
                  {rel.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Export Button */}
        {onExport && (
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            className="ml-auto gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        )}
      </div>
    </div>
  );
};
